import React, { useState } from 'react';
import { Box, Typography, Paper, TextField, Button, CircularProgress, Alert, alpha, useTheme } from '@mui/material';
import DownloadIcon from '@mui/icons-material/DownloadRounded';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremiumRounded';
import { motion } from 'framer-motion';
import { downloadCertificate, getCertificatePdfUrl } from '../api/studentApi';
import { tokens } from '../theme/tokens';
import { fadeInUp } from '../utils/motion';

const MotionPaper = motion(Paper);

// NOTE: no "list my certificates" endpoint was provided yet, so this page is
// entry-by-ID for now. Once a list endpoint exists, swap this for a grid like
// Assignments.jsx and drop the manual ID field.
const Certificates = () => {
  const theme = useTheme();
  const [certificateId, setCertificateId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleDownload = async () => {
    if (!certificateId) return;
    setLoading(true);
    setError('');
    try {
      const res = await downloadCertificate(certificateId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate_${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error(err);
      setError('Certificate not found or download failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>Certificates</Typography>

      <MotionPaper
        variants={fadeInUp} initial="hidden" animate="show"
        elevation={0}
        sx={{
          p: 3.5, borderRadius: '20px', maxWidth: 460,
          border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: 60, height: 60, borderRadius: '16px', display: 'flex', alignItems: 'center',
            justifyContent: 'center', mb: 2.5, color: '#fff',
            background: `linear-gradient(135deg, ${tokens.amber}, ${theme.palette.primary.main})`,
            boxShadow: `0 10px 24px ${alpha(tokens.amber, 0.35)}`,
          }}
        >
          <WorkspacePremiumIcon fontSize="medium" />
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Enter a certificate ID to download or preview it. Once a "my certificates" list is
          available, this page will show all your issued certificates automatically.
        </Typography>

        <TextField
          label="Certificate ID"
          fullWidth
          size="small"
          value={certificateId}
          onChange={(e) => setCertificateId(e.target.value)}
          sx={{ mb: 2 }}
        />

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={!certificateId || loading}
          >
            Download
          </Button>
          <Button
            variant="outlined"
            component="a"
            href={certificateId ? getCertificatePdfUrl(certificateId) : undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!certificateId}
          >
            Preview
          </Button>
        </Box>
      </MotionPaper>
    </Box>
  );
};

export default Certificates;
